import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import { 
  FiDollarSign, 
  FiShoppingBag, 
  FiTrendingUp, 
  FiClock,
  FiArrowUp,
  FiArrowDown,
  FiEye,
  FiCalendar,
  FiDownload,
  FiMoreVertical
} from 'react-icons/fi';
import { BsWallet2, BsGraphUp } from 'react-icons/bs';
import { Helmet } from 'react-helmet-async';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { fetchTransactions } from '../store/slices/paymentSlice';
import { fetchOrders } from '../store/slices/paymentSlice';
import { getProfile } from '../store/slices/authSlice';
import { SupportButton } from '../components/ui/SupportButton';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

export const DashboardPage = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { transactions, orders } = useSelector((state) => state.payments);
  const [timeframe, setTimeframe] = useState('week');

  useEffect(() => {
    dispatch(fetchTransactions());
    dispatch(fetchOrders());
    dispatch(getProfile());
  }, [dispatch]);

  // Calculate statistics
  const totalSpent = orders?.reduce((sum, order) => sum + order.amount, 0) || 0;
  const totalDeposits = transactions?.filter(t => t.type === 'deposit' && t.status === 'success')
    .reduce((sum, t) => sum + t.amount, 0) || 0;
  const pendingTransactions = transactions?.filter(t => ['pending','awaiting_confirmation'].includes(t.status)).length || 0;  // count manual waiting too
  const manualPending = transactions?.filter(t => t.paymentMethod === 'manual' && ['pending','awaiting_confirmation'].includes(t.status)).length || 0;
  const successRate = orders?.length > 0 
    ? ((orders.filter(o => o.status === 'completed').length / orders.length) * 100).toFixed(1)
    : 0;

  // Generate dynamic line chart data
  const generateLineChartData = () => {
    const today = new Date();
    let dates = [];
    let formatStr = 'MMM dd';
    let days = 7;

    if (timeframe === 'week') {
      for (let i = 6; i >= 0; i--) {
        dates.push(subDays(today, i));
      }
      formatStr = 'EEE';
    } else if (timeframe === 'month') {
      days = 30;
      for (let i = 29; i >= 0; i--) {
        dates.push(subDays(today, i));
      }
      formatStr = 'MMM dd';
    } else if (timeframe === 'year') {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      dates = months;
    }

    const spending = {};
    const deposits = {};

    // Initialize
    if (timeframe === 'year') {
      dates.forEach((month, idx) => {
        spending[idx] = 0;
        deposits[idx] = 0;
      });
    } else {
      dates.forEach((date) => {
        const key = format(date, 'yyyy-MM-dd');
        spending[key] = 0;
        deposits[key] = 0;
      });
    }

    // Calculate spending from orders
    if (orders) {
      orders.forEach((order) => {
        const orderDate = new Date(order.createdAt);
        if (timeframe === 'year') {
          const monthIdx = orderDate.getMonth();
          spending[monthIdx] = (spending[monthIdx] || 0) + (order.amount || 0);
        } else {
          const key = format(orderDate, 'yyyy-MM-dd');
          if (spending.hasOwnProperty(key)) {
            spending[key] += order.amount || 0;
          }
        }
      });
    }

    // Calculate deposits from transactions
    if (transactions) {
      transactions.forEach((transaction) => {
        if (transaction.type === 'deposit' && transaction.status === 'success') {
          const txDate = new Date(transaction.createdAt);
          if (timeframe === 'year') {
            const monthIdx = txDate.getMonth();
            deposits[monthIdx] = (deposits[monthIdx] || 0) + (transaction.amount || 0);
          } else {
            const key = format(txDate, 'yyyy-MM-dd');
            if (deposits.hasOwnProperty(key)) {
              deposits[key] += transaction.amount || 0;
            }
          }
        }
      });
    }

    // Format data for chart
    let labels = [];
    let spendingData = [];
    let depositsData = [];

    if (timeframe === 'year') {
      labels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      labels.forEach((_, idx) => {
        spendingData.push(spending[idx] || 0);
        depositsData.push(deposits[idx] || 0);
      });
    } else {
      dates.forEach((date) => {
        const key = format(date, 'yyyy-MM-dd');
        labels.push(format(date, formatStr));
        spendingData.push(spending[key] || 0);
        depositsData.push(deposits[key] || 0);
      });
    }

    return {
      labels,
      datasets: [
        {
          label: 'Spending',
          data: spendingData,
          borderColor: 'rgb(14, 165, 233)',
          backgroundColor: 'rgba(14, 165, 233, 0.1)',
          tension: 0.4,
          fill: true,
        },
        {
          label: 'Deposits',
          data: depositsData,
          borderColor: 'rgb(217, 70, 239)',
          backgroundColor: 'rgba(217, 70, 239, 0.1)',
          tension: 0.4,
          fill: true,
        },
      ],
    };
  };

  // Generate dynamic doughnut chart data
  const generateDoughnutData = () => {
    const platformStats = {};
    const colorMap = {
      'Facebook': 'rgba(14, 165, 233, 0.8)',
      'Instagram': 'rgba(217, 70, 239, 0.8)',
      'Twitter': 'rgba(96, 165, 250, 0.8)',
      'WhatsApp': 'rgba(34, 197, 94, 0.8)',
      'TikTok': 'rgba(236, 72, 153, 0.8)',
    };
    const borderColorMap = {
      'Facebook': 'rgb(14, 165, 233)',
      'Instagram': 'rgb(217, 70, 239)',
      'Twitter': 'rgb(96, 165, 250)',
      'WhatsApp': 'rgb(34, 197, 94)',
      'TikTok': 'rgb(236, 72, 153)',
    };

    // Count platforms from orders
    if (orders) {
      orders.forEach((order) => {
        const platform = order.account?.platform || 'Other';
        platformStats[platform] = (platformStats[platform] || 0) + 1;
      });
    }

    // If no data, show default
    if (Object.keys(platformStats).length === 0) {
      return {
        labels: ['No Data'],
        datasets: [
          {
            data: [1],
            backgroundColor: ['rgba(156, 163, 175, 0.8)'],
            borderColor: ['rgb(156, 163, 175)'],
            borderWidth: 1,
          },
        ],
      };
    }

    const labels = Object.keys(platformStats);
    return {
      labels,
      datasets: [
        {
          data: Object.values(platformStats),
          backgroundColor: labels.map(label => colorMap[label] || 'rgba(156, 163, 175, 0.8)'),
          borderColor: labels.map(label => borderColorMap[label] || 'rgb(156, 163, 175)'),
          borderWidth: 1,
        },
      ],
    };
  };

  const lineChartData = generateLineChartData();
  const doughnutData = generateDoughnutData();

  // Calculate percentage changes
  const calculatePercentageChange = (current, previous) => {
    if (!previous || previous === 0) return 0;
    return (((current - previous) / previous) * 100).toFixed(1);
  };

  const getSpendingChange = () => {
    if (!orders || orders.length === 0) return { change: 0, trend: 'neutral' };
    
    const lineData = generateLineChartData();
    const spendingData = lineData.datasets[0].data;
    const midpoint = Math.floor(spendingData.length / 2);
    
    const firstHalf = spendingData.slice(0, midpoint).reduce((a, b) => a + b, 0);
    const secondHalf = spendingData.slice(midpoint).reduce((a, b) => a + b, 0);
    
    const change = calculatePercentageChange(secondHalf, firstHalf);
    return { 
      change: Math.abs(change), 
      trend: secondHalf >= firstHalf ? 'up' : 'down' 
    };
  };

  const getDepositsChange = () => {
    if (!transactions || transactions.length === 0) return { change: 0, trend: 'neutral' };
    
    const lineData = generateLineChartData();
    const depositsData = lineData.datasets[1].data;
    const midpoint = Math.floor(depositsData.length / 2);
    
    const firstHalf = depositsData.slice(0, midpoint).reduce((a, b) => a + b, 0);
    const secondHalf = depositsData.slice(midpoint).reduce((a, b) => a + b, 0);
    
    const change = calculatePercentageChange(secondHalf, firstHalf);
    return { 
      change: Math.abs(change), 
      trend: secondHalf >= firstHalf ? 'up' : 'down' 
    };
  };

  const getSuccessRateChange = () => {
    if (!orders || orders.length < 2) return { change: 0, trend: 'neutral' };
    
    const sortedOrders = [...orders].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    const midpoint = Math.floor(sortedOrders.length / 2);
    
    const firstHalf = sortedOrders.slice(0, midpoint);
    const secondHalf = sortedOrders.slice(midpoint);
    
    const firstHalfRate = firstHalf.length > 0 
      ? (firstHalf.filter(o => o.status === 'completed').length / firstHalf.length) * 100
      : 0;
    const secondHalfRate = secondHalf.length > 0
      ? (secondHalf.filter(o => o.status === 'completed').length / secondHalf.length) * 100
      : 0;
    
    const change = calculatePercentageChange(secondHalfRate, firstHalfRate);
    return { 
      change: Math.abs(change), 
      trend: secondHalfRate >= firstHalfRate ? 'up' : 'down' 
    };
  };

  const spendingChange = getSpendingChange();
  const depositsChange = getDepositsChange();
  const successRateChange = getSuccessRateChange();

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
      },
    },
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const stats = [
    {
      title: 'Wallet Balance',
      value: `₦${user?.balance?.toLocaleString() || 0}`,
      change: '+0%',
      trend: 'neutral',
      icon: BsWallet2,
      color: 'from-blue-500 to-blue-600',
      bg: 'bg-blue-50',
    },
    {
      title: 'Total Spent',
      value: `₦${totalSpent.toLocaleString()}`,
      change: `${spendingChange.trend === 'up' ? '+' : '-'}${spendingChange.change}%`,
      trend: spendingChange.trend,
      icon: FiShoppingBag,
      color: 'from-purple-500 to-purple-600',
      bg: 'bg-purple-50',
    },
    {
      title: 'Total Deposits',
      value: `₦${totalDeposits.toLocaleString()}`,
      change: `${depositsChange.trend === 'up' ? '+' : '-'}${depositsChange.change}%`,
      trend: depositsChange.trend,
      icon: FiDollarSign,
      color: 'from-green-500 to-green-600',
      bg: 'bg-green-50',
    },
    {
      title: 'Success Rate',
      value: `${successRate}%`,
      change: `${successRateChange.trend === 'up' ? '+' : '-'}${successRateChange.change}%`,
      trend: successRateChange.trend,
      icon: FiTrendingUp,
      color: 'from-orange-500 to-orange-600',
      bg: 'bg-orange-50',
    },
  ];

  if (manualPending > 0) {
    stats.push({
      title: 'Manual Deposits Pending',
      value: `${manualPending}`,
      icon: FiClock,
      color: 'from-yellow-500 to-yellow-600',
      bg: 'bg-yellow-50',
    });
  }

  return (
    <>
      <Helmet>
        <title>Dashboard | Nitro Logs</title>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-luxury-50 via-white to-luxury-100 py-6 sm:py-8">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Welcome Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 sm:mb-8"
          >
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold text-luxury-900">
              Welcome back, <span className="gradient-text">{user?.username}</span>
            </h1>
            <p className="text-xs sm:text-sm md:text-base text-luxury-600 mt-2">
              Here's what's happening with your account today.
            </p>
          </motion.div>

          {/* Stats Grid */}
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-8"
          >
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              const card = (
                <>
                  <div className="flex items-start justify-between mb-3 sm:mb-4">
                    <div className={`w-10 sm:w-12 h-10 sm:h-12 ${stat.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`w-5 sm:w-6 h-5 sm:h-6 text-${stat.color.split('-')[1]}-600`} />
                    </div>
                    <span className={`flex items-center space-x-1 text-xs sm:text-sm flex-shrink-0 ${
                      stat.trend === 'up' ? 'text-green-600' : stat.trend === 'down' ? 'text-red-600' : 'text-luxury-500'
                    }`}>
                      {stat.trend === 'up' ? <FiArrowUp /> : stat.trend === 'down' ? <FiArrowDown /> : null}
                      <span>{stat.change}</span>
                    </span>
                  </div>
                  <h3 className="text-lg sm:text-2xl font-bold text-luxury-900">{stat.value}</h3>
                  <p className="text-xs sm:text-sm text-luxury-500 mt-1">{stat.title}</p>
                </>
              );

              return (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  whileHover={{ y: -5 }}
                  className="glass p-4 sm:p-6 rounded-2xl border border-luxury-200"
                >
                  {stat.title.includes('Manual') ? (
                    <Link to="/transactions" className="block">
                      {card}
                    </Link>
                  ) : (
                    card
                  )}
                </motion.div>
              );
            })}
          </motion.div>

          {/* Charts Row */}
          <div className="grid lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 mb-8">
            {/* Line Chart */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:col-span-2 glass p-4 sm:p-6 rounded-2xl border border-luxury-200 overflow-hidden"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-6">
                <h3 className="text-base sm:text-lg font-semibold text-luxury-900">Activity Overview</h3>
                <div className="flex space-x-2">
                  {['week', 'month', 'year'].map((t) => (
                    <button
                      key={t}
                      onClick={() => setTimeframe(t)}
                      className={`px-2 sm:px-3 py-1 rounded-lg text-xs sm:text-sm transition-colors ${
                        timeframe === t
                          ? 'bg-primary-600 text-white'
                          : 'bg-luxury-100 text-luxury-600 hover:bg-luxury-200'
                      }`}
                    >
                      {t.charAt(0).toUpperCase() + t.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="h-48 sm:h-64">
                <Line data={lineChartData} options={chartOptions} />
              </div>
            </motion.div>

            {/* Doughnut Chart */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="glass p-4 sm:p-6 rounded-2xl border border-luxury-200 overflow-hidden"
            >
              <h3 className="text-base sm:text-lg font-semibold text-luxury-900 mb-4 sm:mb-6">Platform Distribution</h3>
              <div className="h-40 sm:h-48 mb-3 sm:mb-4">
                <Doughnut data={doughnutData} options={{ responsive: true, maintainAspectRatio: false }} />
              </div>
              <div className="space-y-2 text-xs sm:text-sm">
                {doughnutData.labels.map((label, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full`} style={{ backgroundColor: doughnutData.datasets[0].backgroundColor[index] }} />
                      <span className="text-luxury-600">{label}</span>
                    </div>
                    <span className="font-semibold text-luxury-900">{doughnutData.datasets[0].data[index]}%</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Recent Orders and Transactions */}
          <div className="w-full flex flex-col md:flex-row gap-4 sm:gap-6 md:gap-8">
            {/* Recent Orders */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="w-full glass p-4 sm:p-6 rounded-2xl border border-luxury-200"
            >
              <div className="flex items-center justify-between mb-4 sm:mb-6 gap-2">
                <h3 className="text-base sm:text-lg font-semibold text-luxury-900">Recent Orders</h3>
                <Link
                  to="/orders"
                  className="text-primary-600 hover:text-primary-700 text-xs sm:text-sm font-medium flex items-center space-x-1 flex-shrink-0"
                >
                  <span>View All</span>
                  <FiEye className="w-3 sm:w-4 h-3 sm:h-4" />
                </Link>
              </div>

              <div className="space-y-2 sm:space-y-4">
                {orders?.slice(0, 5).map((order, index) => (
                  <motion.div
                    key={order._id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center justify-between gap-2 p-2 sm:p-4 bg-luxury-50 rounded-xl min-w-0"
                  >
                    <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                      <div className="w-8 sm:w-10 h-8 sm:h-10 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FiShoppingBag className="w-4 sm:w-5 h-4 sm:h-5 text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-xs sm:text-sm text-luxury-900 truncate">{order.account?.title || 'Account Purchase'}</p>
                        <p className="text-xs text-luxury-500">
                          {new Date(order.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-bold text-xs sm:text-sm text-luxury-900 text-right">₦{order.amount.toLocaleString()}</p>
                      <span className={`badge text-xs ${
                        order.status === 'completed' ? 'badge-success' : 'badge-warning'
                      }`}>
                        {order.status}
                      </span>
                    </div>
                  </motion.div>
                ))}

                {orders?.length === 0 && (
                  <div className="text-center py-6 sm:py-8">
                    <FiShoppingBag className="w-10 sm:w-12 h-10 sm:h-12 mx-auto text-luxury-300 mb-2 sm:mb-3" />
                    <p className="text-xs sm:text-sm text-luxury-500">No orders yet</p>
                    <Link to="/accounts">
                      <button className="mt-3 text-primary-600 hover:text-primary-700 text-xs sm:text-sm font-medium">
                        Browse Accounts
                      </button>
                    </Link>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Recent Transactions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="w-full glass p-4 sm:p-6 rounded-2xl border border-luxury-200"
            >
              <div className="w-full flex items-center justify-between mb-4 sm:mb-6 gap-2">
                <h3 className="text-base sm:text-lg font-semibold text-luxury-900">Recent Transactions</h3>
                <Link
                  to="/transactions"
                  className="text-primary-600 hover:text-primary-700 text-xs sm:text-sm font-medium flex items-center space-x-1 flex-shrink-0"
                >
                  <span>View All</span>
                  <FiEye className="w-3 sm:w-4 h-3 sm:h-4" />
                </Link>
              </div>

              <div className="space-y-2 sm:space-y-4">
                {transactions?.filter(transaction => transaction.status !== "pending").slice(0, 5).map((transaction, index) => (
                  <motion.div
                    key={transaction._id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center justify-between gap-2 p-2 sm:p-4 bg-luxury-50 rounded-xl min-w-0"
                  >
                    <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                      <div className={`w-8 sm:w-10 h-8 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        transaction.type === 'deposit' ? 'bg-green-100' : 'bg-blue-100'
                      }`}>
                        {transaction.type === 'deposit' ? (
                          <FiArrowDown className={`w-4 sm:w-5 h-4 sm:h-5 text-green-600`} />
                        ) : (
                          <FiArrowUp className={`w-4 sm:w-5 h-4 sm:h-5 text-blue-600`} />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-xs sm:text-sm text-luxury-900 capitalize truncate">{transaction.type}</p>
                        <p className="text-xs text-luxury-500">
                          {new Date(transaction.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-2">
                      <p className={`font-bold text-xs sm:text-sm ${
                        transaction.type === 'deposit' ? 'text-green-600' : 'text-blue-600'
                      }`}>
                        {transaction.type === 'deposit' ? '+' : '-'}₦{transaction.amount.toLocaleString()}
                      </p>
                      <span className={`badge text-xs ${
                        transaction.status === 'success' ? 'badge-success' : 
                        transaction.status === 'pending' ? 'badge-warning' : 'badge-danger'
                      }`}>
                        {transaction.status}
                      </span>
                    </div>
                  </motion.div>
                ))}

                {transactions?.length === 0 && (
                  <div className="text-center py-6 sm:py-8">
                    <FiDollarSign className="w-10 sm:w-12 h-10 sm:h-12 mx-auto text-luxury-300 mb-2 sm:mb-3" />
                    <p className="text-xs sm:text-sm text-luxury-500">No transactions yet</p>
                    <Link to="/deposit">
                      <button className="mt-3 text-primary-600 hover:text-primary-700 text-xs sm:text-sm font-medium">
                        Make a Deposit
                      </button>
                    </Link>
                  </div>
                )}
              </div>
            </motion.div>
          </div>

          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-6 sm:mt-8 grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 md:gap-4"
          >
            <Link to="/accounts">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="w-full glass p-3 sm:p-4 rounded-xl text-center hover:bg-primary-50 transition-all group"
              >
                <FiShoppingBag className="w-5 sm:w-6 h-5 sm:h-6 mx-auto mb-1 sm:mb-2 text-primary-600 group-hover:scale-110 transition-transform" />
                <span className="text-xs sm:text-sm font-medium text-luxury-700 block">Browse Accounts</span>
              </motion.button>
            </Link>
            
            <Link to="/deposit">
              <button
                className="w-full glass p-3 sm:p-4 rounded-xl text-center hover:bg-primary-50 transition-all group"
              >
                <FiDollarSign className="w-5 sm:w-6 h-5 sm:h-6 mx-auto mb-1 sm:mb-2 text-green-600 group-hover:scale-110 transition-transform" />
                <span className="text-xs sm:text-sm font-medium text-luxury-700 block">Make Deposit</span>
              </button>
            </Link>
            
            <Link to="/profile">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="w-full glass p-3 sm:p-4 rounded-xl text-center hover:bg-primary-50 transition-all group"
              >
                <FiEye className="w-5 sm:w-6 h-5 sm:h-6 mx-auto mb-1 sm:mb-2 text-purple-600 group-hover:scale-110 transition-transform" />
                <span className="text-xs sm:text-sm font-medium text-luxury-700 block">View Profile</span>
              </motion.button>
            </Link>
            
            <Link to="/transactions">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="w-full glass p-3 sm:p-4 rounded-xl text-center hover:bg-primary-50 transition-all group"
              >
                <FiClock className="w-5 sm:w-6 h-5 sm:h-6 mx-auto mb-1 sm:mb-2 text-orange-600 group-hover:scale-110 transition-transform" />
                <span className="text-xs sm:text-sm font-medium text-luxury-700 block">History</span>
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </div>
      <SupportButton />
    </>
  );
};